/**
 * Human-readable English labels for gesture names.
 * Matches ai-service domain GestureName enum (gestures.py / GESTURE_SIGNAL_REGISTRY).
 * Used for NEXT Semantica timeline and gesture tables.
 */
export const GESTURE_NAME_TO_LABEL_EN = {
  illustrator_hand_gestures: 'Hand gestures',
  manipulation_hand_massage: 'Hand massage',
  manipulation_touch_nose: 'Nose touch',
  manipulation_touch_index_below_nose: 'Index finger below the nose',
  manipulation_touch_pinch_nose: 'Nose pinch',
  manipulation_touch_mustache: 'Mustache touch',
  manipulation_touch_chin: 'Chin touch',
  manipulation_touch_neck: 'Neck touch',
  manipulation_touch_cranium: 'Cranium touch',
  manipulation_touch_eye: 'Eye touch',
  manipulation_touch_ear: 'Ear touch',
  speech_volume_low: 'Low volume',
  speech_volume_medium: 'Medium volume',
  speech_volume_high: 'High volume',
  speech_tone_low: 'Low tone',
  speech_tone_medium: 'Medium tone',
  speech_tone_high: 'High tone',
  speech_speed_slow: 'Slow speed',
  speech_speed_medium: 'Medium speed',
  speech_speed_fast: 'Fast speed',
  emblem_shrug: 'Shrug',
  emblem_mouth_twist_left: 'Mouth twisted left',
  emblem_mouth_twist_right: 'Mouth twisted right',
  emblem_head_yes: 'Head yes',
  emblem_head_no: 'Head no',
  emblem_head_tilt_left: 'Head tilted left',
  emblem_head_tilt_right: 'Head tilted right',
  gaze_deviation: 'Gaze deviation',
  expression_lips_pressed: 'Lips pressed',
  expression_corner_tight_up: 'Corner tight up',
  expression_brows_down: 'Brows down',
  expression_nose_wrinkled: 'Nose wrinkled',
  expression_upper_lip_inverted_u: 'Upper lip inverted U',
  expression_brows_up: 'Brows up',
  expression_lips_stretched_back: 'Lips stretched back',
  expression_smiling: 'Smiling',
  expression_eyes_wide_open: 'Eyes wide open',
  expression_eyes_closed: 'Eyes closed',
};

// Deleted 2026-09-21 (catalogue mirrors what the detector distinguishes):
//   expression_brows_tight_down, manipulation_nose_touch_base,
//   manipulation_nose_touch_bridge, manipulation_face_touch (umbrella split
//   into one manipulation_touch_<region> class per contact region; it has no
//   single successor). Deliberately NOT aliased below: a stored row with one
//   of these ids is simply dropped by the catalogue-driven readers.
// Retired 2026-09-22: emblem_mouth_twist. The detector's twist ratio is signed,
//   so the class was split into emblem_mouth_twist_left / _right; a stored
//   unsided row cannot be assigned to either, so it is retired, NOT aliased.

/**
 * Gesture ids stored by earlier versions, mapped to their current name.
 * Mirrors LEGACY_GESTURE_NAMES in the ai-service catalog (gestures.py): the A/B/C/D
 * subgroup prefixes were dropped from the expression ids. Node serves the stored
 * gesture_results document verbatim (only the ai-service read path normalizes),
 * so every stored gesture_name that reaches the UI goes through
 * normalizeGestureDocumentNames() at ingestion; analysis-type configs match their
 * saved gestureCatalog rows client-side with currentGestureName().
 */
export const LEGACY_GESTURE_NAME_TO_CURRENT = {
  gesture_a_lips_pressed: 'expression_lips_pressed',
  gesture_b_corner_tight_up: 'expression_corner_tight_up',
  gesture_c_brows_down: 'expression_brows_down',
  gesture_c_nose_wrinkled: 'expression_nose_wrinkled',
  gesture_c_upper_lip_inverted_u: 'expression_upper_lip_inverted_u',
  gesture_d_brows_up_joined: 'expression_brows_up',
  gesture_d_lips_stretched_back: 'expression_lips_stretched_back',
};

/**
 * @param {string} gestureName - Any stored gesture_name
 * @returns {string} - Current gesture_name (unknown names pass through)
 */
export function currentGestureName(gestureName) {
  const key = String(gestureName || '')
    .trim()
    .toLowerCase();
  return LEGACY_GESTURE_NAME_TO_CURRENT[key] || gestureName;
}

function withCurrentGestureName(row) {
  if (!row || typeof row !== 'object') return row;
  const raw = row.gesture_name ?? row.gestureName;
  if (typeof raw !== 'string' || !raw) return row;
  const current = currentGestureName(raw);
  if (current === raw) return row;
  const out = { ...row };
  if ('gesture_name' in row) out.gesture_name = current;
  if ('gestureName' in row) out.gestureName = current;
  return out;
}

/**
 * Rewrite legacy gesture ids to their current name across a stored gesture
 * document (alerts, episodes, coherence alerts and the per-idea gesture
 * breakdown) so feedback rows, habituality joins and labels all use one id.
 * Unknown ids pass through; missing sections are tolerated.
 * @param {Object} doc - gesture_results document as served by Node
 * @returns {Object} - shallow copy with gesture_name normalized
 */
export function normalizeGestureDocumentNames(doc) {
  if (!doc || typeof doc !== 'object') return doc;
  const out = { ...doc };
  if (Array.isArray(doc.alerts)) out.alerts = doc.alerts.map(withCurrentGestureName);
  if (Array.isArray(doc.episodes)) out.episodes = doc.episodes.map(withCurrentGestureName);
  const va = doc.coherence_analysis;
  if (va && typeof va === 'object') {
    const coherence = { ...va };
    if (Array.isArray(va.coherence_alerts)) {
      coherence.coherence_alerts = va.coherence_alerts.map(withCurrentGestureName);
    }
    if (Array.isArray(va.candidates)) {
      coherence.candidates = va.candidates.map((c) =>
        c && Array.isArray(c.gestures)
          ? { ...c, gestures: c.gestures.map(withCurrentGestureName) }
          : c
      );
    }
    out.coherence_analysis = coherence;
  }
  return out;
}

/**
 * @param {string} gestureName - Backend gesture_name (e.g. expression_lips_pressed),
 *   legacy ids accepted
 * @returns {string} - English label for display
 */
export function gestureNameToLabel(gestureName) {
  if (!gestureName || typeof gestureName !== 'string') return gestureName || '-';
  const key = currentGestureName(gestureName).toLowerCase().trim();
  return (
    GESTURE_NAME_TO_LABEL_EN[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/**
 * @param {string} classification - 'gesture' | 'sustained'
 * @returns {string} - English label for display
 */
export function classificationToLabel(classification) {
  if (classification === 'gesture') return 'Gesture';
  if (classification === 'sustained') return 'Sustained';
  return classification || 'Sustained';
}
